type GuestSlug = 'ma' | 'james';
type AccessStatus = 'enabled' | 'disabled' | 'pending';
type ConfirmAction = 'disable' | 'regenerate' | 'reset-pin' | 'deny-request';

interface GuestDef {
  slug: GuestSlug;
  name: string;
  categoryName: string;
  textChannelName: string;
  callChannelName: string;
}

interface GuestControlState {
  accessEnabled: boolean;
  pinSet: boolean;
  linkGenerated: boolean;
  linkValue: string;
  pendingRequest: boolean;
  lastActivity: string;
  spaceEnsured: boolean;
  copied: boolean;
}

type GuestStateMap = Record<GuestSlug, GuestControlState>;

interface PendingConfirmation {
  action: ConfirmAction;
  slug: GuestSlug;
}

const CONTROLS_PATH = '/people/test/guest-controls';
const GUEST_PREVIEW_BASE = '/people/test/guest-preview';
const STORAGE_KEY = 'hub.people.guestControlsTest.v2';
const GUESTS: GuestDef[] = [
  { slug: 'ma', name: 'Ma', categoryName: 'Ma Category', textChannelName: 'Ma text', callChannelName: 'Ma call' },
  { slug: 'james', name: 'James', categoryName: 'James Category', textChannelName: 'James text', callChannelName: 'James call' },
];

let state: GuestStateMap = defaultState();
let confirmation: PendingConfirmation | null = null;
let highlightedSlug: GuestSlug | null = null;

export function isGuestControlsTestPath(): boolean {
  return window.location.pathname === CONTROLS_PATH;
}

export function renderGuestControlsTestPage(): void {
  mountGuestControlsStyles();
  document.body.classList.add('guest-controls-v2-body');
  state = loadState();
  confirmation = null;
  highlightedSlug = null;
  paint();
}

function paint(): void {
  const app = document.getElementById('app');
  if (!app) return;
  app.innerHTML = `
    <main class="gct-shell">
      <section class="gct-banner" aria-label="Test controls notice">
        <div class="gct-kicker">TEST CONTROLS</div>
        <div>Mock state — localStorage only — no backend</div>
      </section>
      <header class="gct-page-head">
        <h1>Guest Access Controls</h1>
        <p>Mitchell controls preview for Ma and James guest access.</p>
      </header>
      <section class="gct-grid" aria-label="Guest controls">
        ${GUESTS.map((guest) => renderGuestCard(guest)).join('')}
      </section>
      ${renderCategorySettingsPreview()}
      ${confirmation ? renderConfirmModal(confirmation) : ''}
    </main>
  `;
  wireControls();
}

function renderGuestCard(guest: GuestDef): string {
  const row = state[guest.slug];
  const accessStatus = accessStatusFor(row);
  return `
    <section class="gct-card ${highlightedSlug === guest.slug ? 'gct-highlight' : ''}" id="gct-card-${escapeAttr(guest.slug)}" data-guest-slug="${escapeAttr(guest.slug)}">
      <header class="gct-card-head">
        <div>
          <h2>${escapeHtml(guest.name)}</h2>
          <p>${escapeHtml(guest.categoryName)} guest controls</p>
        </div>
        <span class="gct-badge gct-badge-${escapeAttr(accessStatus)}">${escapeHtml(accessStatusLabel(accessStatus))}</span>
      </header>
      <div class="gct-status-grid">
        ${renderStatusItem('Access status', accessStatusLabel(accessStatus), accessStatus)}
        ${renderStatusItem('Current PIN status', row.pinSet ? 'Set' : 'Not set', row.pinSet ? 'enabled' : 'disabled')}
        ${renderStatusItem('Link status', row.linkGenerated ? 'Generated' : 'Not generated', row.linkGenerated ? 'enabled' : 'disabled')}
        ${renderStatusItem('Last activity', row.lastActivity, 'neutral')}
      </div>
      <div class="gct-link-panel">
        <label for="gct-link-${escapeAttr(guest.slug)}">Guest link</label>
        <div class="gct-link-row">
          <input id="gct-link-${escapeAttr(guest.slug)}" value="${escapeAttr(row.linkValue || 'No link generated')}" readonly>
          <button type="button" class="gct-btn" data-action="copy-link" ${row.linkGenerated ? '' : 'disabled'}>${iconCopy()}<span>Copy Link</span></button>
        </div>
        ${row.copied ? '<div class="gct-copy-note">Link copied to clipboard mock.</div>' : ''}
      </div>
      <div class="gct-actions">
        <button type="button" class="gct-btn gct-btn-primary" data-action="generate-link">${iconLink()}<span>Generate Link</span></button>
        <button type="button" class="gct-btn" data-action="regenerate-link" ${row.linkGenerated ? '' : 'disabled'}>${iconRefresh()}<span>Regenerate Link</span></button>
        ${row.accessEnabled ? `<button type="button" class="gct-btn gct-btn-danger" data-action="disable-access">${iconShieldOff()}<span>Disable Access</span></button>` : `<button type="button" class="gct-btn gct-btn-primary" data-action="reenable-access">${iconShieldCheck()}<span>Re-enable Access</span></button>`}
        <button type="button" class="gct-btn" data-action="reset-pin">${iconKey()}<span>Reset PIN</span></button>
        ${row.pendingRequest ? `<button type="button" class="gct-btn gct-btn-primary" data-action="approve-request">${iconCheck()}<span>Approve Request</span></button><button type="button" class="gct-btn gct-btn-danger" data-action="deny-request">${iconX()}<span>Deny Request</span></button>` : ''}
        <button type="button" class="gct-btn" data-action="ensure-space">${iconLayers()}<span>Ensure Space</span></button>
        <a class="gct-btn gct-link-button" href="${escapeAttr(previewUrl(guest.slug))}">${iconExternal()}<span>Open ${escapeHtml(guest.name)} Preview</span></a>
      </div>
      <div class="gct-space-status">${row.spaceEnsured ? 'Space creation status: ensured in mock state.' : 'Space creation status: not yet ensured in mock state.'}</div>
    </section>
  `;
}

function renderStatusItem(label: string, value: string, status: AccessStatus | 'neutral'): string {
  return `
    <div class="gct-status-item">
      <span>${escapeHtml(label)}</span>
      <strong>${escapeHtml(value)}</strong>
      <i class="gct-dot gct-dot-${escapeAttr(status)}" aria-hidden="true"></i>
    </div>
  `;
}

function renderCategorySettingsPreview(): string {
  return `
    <section class="gct-category-preview" aria-label="Category settings preview">
      <div class="gct-category-head">
        <div>
          <h2>Category Settings Preview</h2>
          <p>Category-level settings will integrate with backend category management when deployed</p>
        </div>
      </div>
      <div class="gct-category-grid">
        ${GUESTS.map((guest) => renderCategoryCard(guest)).join('')}
      </div>
    </section>
  `;
}

function renderCategoryCard(guest: GuestDef): string {
  const row = state[guest.slug];
  return `
    <article class="gct-category-card">
      <h3>${escapeHtml(guest.categoryName)}</h3>
      <dl>
        <div><dt>Category name</dt><dd>${escapeHtml(guest.name)}</dd></div>
        <div><dt>Channels list</dt><dd>${escapeHtml(guest.textChannelName)}, ${escapeHtml(guest.callChannelName)}</dd></div>
        <div><dt>Guest access status</dt><dd>${escapeHtml(accessStatusLabel(accessStatusFor(row)))}</dd></div>
      </dl>
      <button type="button" class="gct-btn" data-scroll-guest="${escapeAttr(guest.slug)}">${iconSettings()}<span>Open Category Settings</span></button>
    </article>
  `;
}

function renderConfirmModal(pending: PendingConfirmation): string {
  const guest = guestBySlug(pending.slug);
  const copy = confirmationCopy(pending.action, guest.name);
  return `
    <div class="gct-modal-backdrop" role="presentation">
      <section class="gct-modal" role="dialog" aria-modal="true" aria-labelledby="gct-modal-title">
        <h2 id="gct-modal-title">${escapeHtml(copy.title)}</h2>
        <p>${escapeHtml(copy.body)}</p>
        <div class="gct-modal-actions">
          <button type="button" class="gct-btn" data-modal-action="cancel">Cancel</button>
          <button type="button" class="gct-btn gct-btn-danger" data-modal-action="confirm">${escapeHtml(copy.confirmLabel)}</button>
        </div>
      </section>
    </div>
  `;
}

function wireControls(): void {
  document.querySelectorAll<HTMLButtonElement>('[data-action]').forEach((button) => {
    button.addEventListener('click', () => {
      const card = button.closest<HTMLElement>('[data-guest-slug]');
      const slug = card?.dataset['guestSlug'];
      const action = button.dataset['action'];
      if (!isGuestSlug(slug) || !action) return;
      handleAction(slug, action);
    });
  });
  document.querySelectorAll<HTMLButtonElement>('[data-scroll-guest]').forEach((button) => {
    button.addEventListener('click', () => {
      const slug = button.dataset['scrollGuest'];
      if (!isGuestSlug(slug)) return;
      highlightedSlug = slug;
      paint();
      document.getElementById(`gct-card-${slug}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  });
  document.querySelectorAll<HTMLButtonElement>('[data-modal-action]').forEach((button) => {
    button.addEventListener('click', () => {
      if (button.dataset['modalAction'] === 'confirm' && confirmation) {
        performConfirmedAction(confirmation);
      }
      confirmation = null;
      paint();
    });
  });
}

function handleAction(slug: GuestSlug, action: string): void {
  highlightedSlug = slug;
  if (action === 'generate-link') {
    updateGuest(slug, (row) => ({
      ...row,
      linkGenerated: true,
      linkValue: generateMockLink(slug),
      copied: false,
      lastActivity: timestampLabel(),
    }));
  } else if (action === 'copy-link') {
    copyMockLink(slug);
  } else if (action === 'disable-access') {
    confirmation = { action: 'disable', slug };
  } else if (action === 'reenable-access') {
    updateGuest(slug, (row) => ({ ...row, accessEnabled: true, pendingRequest: false, lastActivity: timestampLabel() }));
  } else if (action === 'regenerate-link') {
    confirmation = { action: 'regenerate', slug };
  } else if (action === 'reset-pin') {
    confirmation = { action: 'reset-pin', slug };
  } else if (action === 'approve-request') {
    updateGuest(slug, (row) => ({ ...row, accessEnabled: true, pendingRequest: false, lastActivity: timestampLabel() }));
  } else if (action === 'deny-request') {
    confirmation = { action: 'deny-request', slug };
  } else if (action === 'ensure-space') {
    updateGuest(slug, (row) => ({ ...row, spaceEnsured: true, lastActivity: timestampLabel() }));
  }
  saveState();
  paint();
}

function performConfirmedAction(pending: PendingConfirmation): void {
  if (pending.action === 'disable') {
    updateGuest(pending.slug, (row) => ({
      ...row,
      accessEnabled: false,
      pendingRequest: false,
      copied: false,
      lastActivity: timestampLabel(),
    }));
  } else if (pending.action === 'regenerate') {
    updateGuest(pending.slug, (row) => ({
      ...row,
      linkGenerated: true,
      linkValue: generateMockLink(pending.slug),
      copied: false,
      lastActivity: timestampLabel(),
    }));
  } else if (pending.action === 'reset-pin') {
    updateGuest(pending.slug, (row) => ({
      ...row,
      pinSet: false,
      lastActivity: timestampLabel(),
    }));
  } else if (pending.action === 'deny-request') {
    updateGuest(pending.slug, (row) => ({
      ...row,
      accessEnabled: false,
      pendingRequest: false,
      lastActivity: timestampLabel(),
    }));
  }
  saveState();
}

function copyMockLink(slug: GuestSlug): void {
  const value = state[slug].linkValue;
  if (value && navigator.clipboard?.writeText) {
    void navigator.clipboard.writeText(value).catch(() => undefined);
  }
  updateGuest(slug, (row) => ({ ...row, copied: true, lastActivity: timestampLabel() }));
}

function updateGuest(slug: GuestSlug, updater: (row: GuestControlState) => GuestControlState): void {
  state = { ...state, [slug]: updater(state[slug]) };
}

function accessStatusFor(row: GuestControlState): AccessStatus {
  if (row.pendingRequest) return 'pending';
  return row.accessEnabled ? 'enabled' : 'disabled';
}

function accessStatusLabel(status: AccessStatus): string {
  if (status === 'enabled') return 'Enabled';
  if (status === 'pending') return 'Pending Request';
  return 'Disabled';
}

function confirmationCopy(action: ConfirmAction, guestName: string): { title: string; body: string; confirmLabel: string } {
  if (action === 'disable') {
    return {
      title: `Disable ${guestName} access`,
      body: `${guestName} will not be able to open Mitchell Space until access is re-enabled.`,
      confirmLabel: 'Disable Access',
    };
  }
  if (action === 'regenerate') {
    return {
      title: `Regenerate ${guestName} link`,
      body: 'The old link stops working after a new mock link is generated.',
      confirmLabel: 'Regenerate Link',
    };
  }
  if (action === 'reset-pin') {
    return {
      title: `Reset ${guestName} PIN`,
      body: `${guestName} will need to create a new PIN before returning.`,
      confirmLabel: 'Reset PIN',
    };
  }
  return {
    title: `Deny ${guestName} request`,
    body: 'The access request will be cleared and access will remain disabled.',
    confirmLabel: 'Deny Request',
  };
}

function defaultState(): GuestStateMap {
  return {
    ma: {
      accessEnabled: true,
      pinSet: true,
      linkGenerated: true,
      linkValue: generateMockLink('ma'),
      pendingRequest: false,
      lastActivity: 'Today, 9:12 AM',
      spaceEnsured: true,
      copied: false,
    },
    james: {
      accessEnabled: false,
      pinSet: false,
      linkGenerated: false,
      linkValue: '',
      pendingRequest: true,
      lastActivity: 'Yesterday, 4:38 PM',
      spaceEnsured: false,
      copied: false,
    },
  };
}

function loadState(): GuestStateMap {
  const fallback = defaultState();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<Record<GuestSlug, Partial<GuestControlState>>>;
    return {
      ma: normalizeGuestState(parsed.ma, fallback.ma),
      james: normalizeGuestState(parsed.james, fallback.james),
    };
  } catch {
    return fallback;
  }
}

function normalizeGuestState(value: Partial<GuestControlState> | undefined, fallback: GuestControlState): GuestControlState {
  if (!value) return fallback;
  return {
    accessEnabled: typeof value.accessEnabled === 'boolean' ? value.accessEnabled : fallback.accessEnabled,
    pinSet: typeof value.pinSet === 'boolean' ? value.pinSet : fallback.pinSet,
    linkGenerated: typeof value.linkGenerated === 'boolean' ? value.linkGenerated : fallback.linkGenerated,
    linkValue: typeof value.linkValue === 'string' ? value.linkValue : fallback.linkValue,
    pendingRequest: typeof value.pendingRequest === 'boolean' ? value.pendingRequest : fallback.pendingRequest,
    lastActivity: typeof value.lastActivity === 'string' ? value.lastActivity : fallback.lastActivity,
    spaceEnsured: typeof value.spaceEnsured === 'boolean' ? value.spaceEnsured : fallback.spaceEnsured,
    copied: typeof value.copied === 'boolean' ? value.copied : false,
  };
}

function saveState(): void {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

function generateMockLink(slug: GuestSlug): string {
  const token = `mock-${slug}-${Date.now().toString(36)}`;
  return `${window.location.origin}${GUEST_PREVIEW_BASE}/${encodeURIComponent(slug)}?token=${encodeURIComponent(token)}`;
}

function previewUrl(slug: GuestSlug): string {
  return `${GUEST_PREVIEW_BASE}/${slug}`;
}

function timestampLabel(): string {
  return new Date().toLocaleString([], { dateStyle: 'short', timeStyle: 'short' });
}

function guestBySlug(slug: GuestSlug): GuestDef {
  const found = GUESTS.find((guest) => guest.slug === slug);
  if (found) return found;
  return { slug: 'ma', name: 'Ma', categoryName: 'Ma Category', textChannelName: 'Ma text', callChannelName: 'Ma call' };
}

function isGuestSlug(value: string | undefined): value is GuestSlug {
  return value === 'ma' || value === 'james';
}

function escapeHtml(value: string): string {
  const element = document.createElement('div');
  element.textContent = value;
  return element.innerHTML;
}

function escapeAttr(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}

function svg(body: string): string {
  return `<svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${body}</svg>`;
}

function iconCheck(): string { return svg('<path d="M20 6L9 17l-5-5"/>'); }
function iconCopy(): string { return svg('<rect x="9" y="9" width="13" height="13" rx="2"/><rect x="2" y="2" width="13" height="13" rx="2"/>'); }
function iconExternal(): string { return svg('<path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><path d="M15 3h6v6"/><path d="M10 14L21 3"/>'); }
function iconKey(): string { return svg('<circle cx="7.5" cy="15.5" r="5.5"/><path d="M12 11l9-9"/><path d="M17 6l2 2"/><path d="M14 9l2 2"/>'); }
function iconLayers(): string { return svg('<path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>'); }
function iconLink(): string { return svg('<path d="M10 13a5 5 0 0 0 7.07 0l2.83-2.83a5 5 0 0 0-7.07-7.07L11 4.93"/><path d="M14 11a5 5 0 0 0-7.07 0L4.1 13.83a5 5 0 0 0 7.07 7.07L13 19.07"/>'); }
function iconRefresh(): string { return svg('<path d="M21 12a9 9 0 0 1-15.5 6.2L3 16"/><path d="M3 21v-5h5"/><path d="M3 12A9 9 0 0 1 18.5 5.8L21 8"/><path d="M21 3v5h-5"/>'); }
function iconSettings(): string { return svg('<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06A1.65 1.65 0 0 0 15 19.4a1.65 1.65 0 0 0-1 .6 1.65 1.65 0 0 0-.33 1.82V22a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 20.4a1.65 1.65 0 0 0-1.82-.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-.6-1 1.65 1.65 0 0 0-1.82-.33H2a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 3.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-.6 1.65 1.65 0 0 0 .33-1.82V2a2 2 0 1 1 4 0v.09A1.65 1.65 0 0 0 15 3.6a1.65 1.65 0 0 0 1.82.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9c.2.36.5.66.86.86.24.14.51.22.79.24H22a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51.9z"/>'); }
function iconShieldCheck(): string { return svg('<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/>'); }
function iconShieldOff(): string { return svg('<path d="M2 2l20 20"/><path d="M12 22s8-4 8-10V5l-8-3-5.2 1.95"/><path d="M4 5.5V12c0 6 8 10 8 10"/>'); }
function iconX(): string { return svg('<path d="M18 6L6 18"/><path d="M6 6l12 12"/>'); }

function mountGuestControlsStyles(): void {
  const styleId = 'guest-controls-test-v2-styles';
  if (document.getElementById(styleId)) return;
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
.guest-controls-v2-body #app { min-height: 100dvh; background: var(--surface-2); color: var(--text-primary); }
.gct-shell { width: min(100%, 1180px); margin: 0 auto; padding: 14px; box-sizing: border-box; display: grid; gap: 14px; }
.gct-banner, .gct-page-head, .gct-card, .gct-category-preview, .gct-category-card { border: 1px solid var(--border-subtle); background: var(--surface-1); border-radius: var(--radius-md); }
.gct-banner { display: grid; gap: 4px; padding: 12px; color: var(--text-secondary); }
.gct-kicker { color: var(--warning); font-weight: 900; letter-spacing: 0; }
.gct-page-head { padding: 14px; }
.gct-page-head h1, .gct-card h2, .gct-category-preview h2, .gct-category-card h3, .gct-modal h2 { margin: 0; color: var(--text-primary); letter-spacing: 0; }
.gct-page-head p, .gct-card p, .gct-category-preview p, .gct-modal p { margin: 4px 0 0; color: var(--text-secondary); line-height: 1.45; }
.gct-grid { display: grid; gap: 14px; grid-template-columns: repeat(auto-fit, minmax(310px, 1fr)); align-items: start; }
.gct-card { display: grid; gap: 14px; padding: 14px; scroll-margin: 20px; }
.gct-highlight { border-color: var(--accent); box-shadow: 0 0 0 2px var(--accent-soft); }
.gct-card-head { display: flex; justify-content: space-between; gap: 12px; align-items: flex-start; }
.gct-badge { display: inline-flex; align-items: center; min-height: 26px; padding: 2px 9px; border-radius: var(--radius-sm); font-size: 12px; font-weight: 900; color: var(--fg-on-accent); white-space: nowrap; }
.gct-badge-enabled { background: var(--accent); }
.gct-badge-disabled { background: var(--danger); }
.gct-badge-pending { background: var(--warning); }
.gct-status-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
.gct-status-item { position: relative; min-height: 78px; display: grid; align-content: center; gap: 5px; padding: 10px; border: 1px solid var(--border-subtle); border-radius: var(--radius-sm); background: var(--surface-2); }
.gct-status-item span { color: var(--text-muted); font-size: 12px; }
.gct-status-item strong { color: var(--text-primary); overflow-wrap: anywhere; }
.gct-dot { position: absolute; top: 10px; right: 10px; width: 9px; height: 9px; border-radius: var(--radius-sm); background: var(--text-muted); }
.gct-dot-enabled { background: var(--accent); }
.gct-dot-disabled { background: var(--danger); }
.gct-dot-pending { background: var(--warning); }
.gct-link-panel { display: grid; gap: 7px; }
.gct-link-panel label { color: var(--text-secondary); font-size: 13px; font-weight: 800; }
.gct-link-row { display: grid; grid-template-columns: 1fr auto; gap: 8px; }
.gct-link-row input { min-width: 0; width: 100%; box-sizing: border-box; border: 1px solid var(--border-subtle); border-radius: var(--radius-sm); background: var(--surface-2); color: var(--text-primary); padding: 11px; font: inherit; }
.gct-copy-note, .gct-space-status { color: var(--text-muted); font-size: 13px; line-height: 1.45; }
.gct-actions { display: grid; gap: 8px; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); }
.gct-btn { min-height: 42px; border: 1px solid var(--border-subtle); border-radius: var(--radius-sm); background: var(--surface-2); color: var(--text-primary); display: inline-flex; align-items: center; justify-content: center; gap: 8px; padding: 8px 10px; font: inherit; text-align: center; text-decoration: none; cursor: pointer; letter-spacing: 0; }
.gct-btn:hover { background: var(--surface-2-hover); }
.gct-btn:disabled { color: var(--text-muted); cursor: not-allowed; background: var(--surface-1); }
.gct-btn svg { flex: 0 0 auto; }
.gct-btn-primary { background: var(--accent); color: var(--fg-on-accent); border-color: var(--accent); }
.gct-btn-primary:hover { background: var(--accent-hover); }
.gct-btn-danger { background: var(--danger); color: var(--fg-on-accent); border-color: var(--danger); }
.gct-link-button { color: var(--link); }
.gct-category-preview { display: grid; gap: 14px; padding: 14px; }
.gct-category-head { display: flex; justify-content: space-between; gap: 12px; }
.gct-category-grid { display: grid; gap: 14px; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); }
.gct-category-card { display: grid; gap: 12px; padding: 14px; background: var(--surface-2); }
.gct-category-card dl { display: grid; gap: 8px; margin: 0; }
.gct-category-card div { display: grid; gap: 2px; }
.gct-category-card dt { color: var(--text-muted); font-size: 12px; }
.gct-category-card dd { margin: 0; color: var(--text-primary); line-height: 1.35; }
.gct-modal-backdrop { position: fixed; inset: 0; display: grid; place-items: center; padding: 16px; background: var(--surface-0); z-index: 1000; }
.gct-modal { width: min(100%, 420px); display: grid; gap: 14px; border: 1px solid var(--border-strong); border-radius: var(--radius-md); background: var(--surface-1); padding: 18px; box-shadow: 0 18px 44px var(--surface-0); }
.gct-modal-actions { display: flex; justify-content: flex-end; gap: 10px; flex-wrap: wrap; }
@media (max-width: 640px) {
  .gct-shell { padding: 10px; }
  .gct-grid, .gct-status-grid, .gct-actions, .gct-category-grid, .gct-link-row { grid-template-columns: 1fr; }
  .gct-card-head { display: grid; }
}
`;
  document.head.appendChild(style);
}
